import {
  Arrange,
  Button,
  Container,
  Spacer,
  Split,
  SplitSection,
  Text,
  TextButton,
} from '@loomhq/lens';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setShowBlockedNotificationsModal } from 'actions/notification-settings';
import { PERMISSION_BANNER_LIBRARY } from 'constants/banner-types';
import {
  CLICKED_ALLOW_NOTIFICATIONS,
  CLICKED_DISMISS_NOTIFICATIONS,
  REQUEST_PERMISSIONS_RESULT,
} from 'constants/events';
import {
  PERMISSION_DEFAULT,
  PERMISSION_DENIED,
  PERMISSION_GRANTED,
} from 'constants/notifications';
import { SHOW_WEB_PERMISSIONS_PROMPT } from 'constants/triggers';
import { reenableWebNotifications } from 'creators/notification-settings';
import { completeTriggerCreator } from 'creators/triggers';
import * as analytics from 'utilities/analytics';
import { setupPushNotifications } from 'utilities/notifications';

import { useShouldShowTrigger, useUrlTriggerReset } from 'hooks/triggers';
import { useCheckIfSdkSharedUser } from 'hooks/workspace';

import { useBackfillPushToken } from '../hooks/useBackfillPushToken';

import BlockedNotificationsModal from 'components/blocked-notifications-modal';

import styles from './styles.module.less';

const PermissionsIcon = () => {
  return (
    <svg
      width="56"
      height="56"
      viewBox="0 0 56 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0V56H56V0H0ZM50.4 50.4H5.6V5.6H50.4V50.4Z" fill="#ED785E" />
      <path
        d="M48.72 7.28003H30.0608C34.7704 8.13683 38.4608 11.872 39.2784 16.5928H48.72V7.28003Z"
        fill="#ED785E"
      />
      <path
        d="M25.9392 7.28003H7.28003V16.5928H16.7216C17.5336 11.872 21.2296 8.13683 25.9392 7.28003Z"
        fill="#ED785E"
      />
      <path
        d="M7.28003 18.2728V27.16H17.4664C19.2136 23.0608 23.2624 20.1768 28 20.1768C32.7376 20.1768 36.7864 23.0608 38.5336 27.16H48.72V18.2728H7.28003Z"
        fill="#ED785E"
      />
      <path
        d="M7.28003 37.7272H18.704C20.7816 34.8432 24.1696 32.956 27.9944 32.956C31.8248 32.956 35.2072 34.8376 37.2848 37.7272H48.72V28.84H7.28003V37.7272Z"
        fill="#ED785E"
      />
      <path
        d="M7.28003 48.72H20.4232C22.4448 46.9392 25.0936 45.8528 28 45.8528C30.9064 45.8528 33.5552 46.9336 35.5768 48.72H48.72V39.4072H7.28003V48.72Z"
        fill="#ED785E"
      />
    </svg>
  );
};

const WebPermissionsBanner = ({
  bannerLocation = PERMISSION_BANNER_LIBRARY,
}) => {
  const showWebPermissions = useShouldShowTrigger(SHOW_WEB_PERMISSIONS_PROMPT);

  const dispatch = useDispatch();

  useBackfillPushToken();

  useUrlTriggerReset({
    paramName: 'permissionsOverride',
    trigger: SHOW_WEB_PERMISSIONS_PROMPT,
  });

  const isSdkSharedUser = useCheckIfSdkSharedUser();

  const hasRecordedFirstVideo = useSelector(
    state => state.checklist.first_video_recording
  );

  if (
    isSdkSharedUser ||
    !showWebPermissions ||
    !hasRecordedFirstVideo ||
    !('Notification' in window) ||
    Notification.permission !== PERMISSION_DEFAULT
  ) {
    return null;
  }

  const onClickAllow = () => {
    analytics.track(CLICKED_ALLOW_NOTIFICATIONS, {
      location: bannerLocation,
    });

    setupPushNotifications(bannerLocation).then(result => {
      if (result === PERMISSION_GRANTED) {
        dispatch(reenableWebNotifications());
      }

      if (result === PERMISSION_DENIED) {
        dispatch(setShowBlockedNotificationsModal(true));

        return;
      }

      dispatch(completeTriggerCreator(SHOW_WEB_PERMISSIONS_PROMPT));

      analytics.track(REQUEST_PERMISSIONS_RESULT, {
        location: bannerLocation,
        result,
      });
    });
  };
  const onClickDismiss = () => {
    // Fade out and then fire action
    dispatch(completeTriggerCreator(SHOW_WEB_PERMISSIONS_PROMPT));

    analytics.track(CLICKED_DISMISS_NOTIFICATIONS, {
      location: bannerLocation,
    });
  };

  const showClassicDesign = bannerLocation === PERMISSION_BANNER_LIBRARY;

  if (showClassicDesign) {
    return (
      <>
        <Container
          radius="medium"
          padding="large"
          borderSide="all"
          backgroundColor="backgroundSecondary"
          borderColor="border"
          className={styles.permissionsBanner}
        >
          <Split
            justifyContent="space-between"
            gap="medium"
            alignItems="flex-start"
          >
            <SplitSection>
              <PermissionsIcon />
            </SplitSection>
            <SplitSection grow={1} basis={52}>
              <Split
                justifyContent="space-between"
                alignItems="center"
                gap="medium"
              >
                <SplitSection>
                  <Arrange gap="medium">
                    <Arrange gap="small" autoFlow="row">
                      <Text size="large" fontWeight="black">
                        You need to allow permission for notifications
                      </Text>
                      <Text>
                        Know when your video has been watched and interacted
                        with.
                      </Text>
                    </Arrange>
                  </Arrange>
                </SplitSection>
                <SplitSection>
                  <Split gap="small">
                    <SplitSection>
                      <div className={styles.btnWrapper}>
                        <Button onClick={() => onClickAllow()}>
                          Allow notifications
                        </Button>
                      </div>
                    </SplitSection>
                    <SplitSection>
                      <TextButton onClick={() => onClickDismiss()}>
                        Dismiss
                      </TextButton>
                    </SplitSection>
                  </Split>
                </SplitSection>
              </Split>
            </SplitSection>
          </Split>
        </Container>
        <Spacer bottom="large" />
      </>
    );
  }

  return (
    <Container
      radius="medium"
      backgroundColor="blurpleLight"
      className={styles.permissionsBanner}
    >
      <BlockedNotificationsModal location={bannerLocation} />
      <Split gap="medium" justifyContent="space-between">
        <SplitSection grow={1} basis={72}>
          <Arrange align="center" gap="medium">
            <Text>🔔</Text>
            <Text>
              <strong>Turn on notifications</strong> to know when your video has
              been watched and interacted with.
            </Text>
          </Arrange>
        </SplitSection>
        <SplitSection>
          <Arrange gap="small">
            <Button onClick={() => onClickAllow()}>Allow notifications</Button>
            <TextButton onClick={() => onClickDismiss()}>Dismiss</TextButton>
          </Arrange>
        </SplitSection>
      </Split>
    </Container>
  );
};

export default WebPermissionsBanner;
